import {
  getTrackingCartItemPurchaseMode,
  getTrackingPromotionLabel,
} from '@/feature/cart/services/cart.tracking'
import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  type Availability,
  ProductAnalyticsDataDocument,
  type ProductCard_ProductPriceAndStockFragment,
  type ProductCardFragment,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useCallback, useRef } from 'react'
import type { MergedArticle } from '../types/mergedProduct'

gql(/* GraphQL */ `
  query ProductAnalyticsData($reference: String!) {
    productByReference(reference: $reference) {
      reference
      breadcrumbs {
        name
        urlKey
      }
      analytics {
        promotionStatus
        profileCase
      }
      parentReference
      nomenclatureFamily
      nomenclatureGroup
    }
  }
`)

type Product = (ProductCardFragment & ProductCard_ProductPriceAndStockFragment) | MergedArticle

type ProductAnalyticsParams = {
  product: Product
  position?: number
  isAuthenticated: boolean
  source?: string
}

/**
 * Hook for tracking product card analytics events
 */
export function useProductCardAnalytics({
  product,
  position,
  isAuthenticated,
  source,
}: ProductAnalyticsParams) {
  const { config } = useSiteConfig()

  const isTrackingEnabled = useAtomValue(trackingConsentAtom)

  const [fetchAnalyticsData] = useLazyQuery(ProductAnalyticsDataDocument, {
    fetchPolicy: 'cache-first',
  })

  // Track if we've already pushed to data layer
  const hasTrackedRef = useRef(false)

  // Callback to trigger when user clicks
  const trackProductPageAnalytics = useCallback(async () => {
    if (!isTrackingEnabled) return

    // Don't track if already done
    if (hasTrackedRef.current) {
      return
    }

    // Fetch analytics data and breadcrumbs
    const { data } = await fetchAnalyticsData({
      variables: { reference: product.reference },
    })

    // Get analytics from query result, fallback to product
    const analytics = data?.productByReference?.analytics

    // Wait until analytics are available
    if (!analytics) {
      return
    }

    const item_category = data?.productByReference?.nomenclatureFamily || ''
    const item_category2 = data?.productByReference?.nomenclatureGroup || ''
    const item_code_p = product.parentReference || data?.productByReference?.parentReference || ''
    const productAvailability: Array<Availability | null> = product.stock?.availability ?? []

    const item_purchase_mode = product
      ? getTrackingCartItemPurchaseMode(
          productAvailability,
          product.canAddToCart || false,
          isAuthenticated,
        )
      : ''

    if (product.price || productAvailability.length > 0) {
      pushToGtmDataLayer({
        event: 'select_item',
        ecommerce: {
          currency: 'EUR',
          items: [
            {
              item_id: product.reference,
              item_code_p,
              item_nature: product?.type?.toLowerCase() || 'article',
              item_name: product.name || '',
              item_price:
                ('bestArticlePrice' in product ? product.bestArticlePrice : null) ||
                product.price?.exclTax ||
                0,
              item_index: position?.toString() || '',
              item_brand: product.brand?.name || '',
              item_category,
              item_category2,
              item_list_name: source || '',
              item_promotion: getTrackingPromotionLabel(analytics.promotionStatus),
              item_public_price: String(
                config?.catalog?.isPublicPricesEnabled && !isAuthenticated ? 'true' : 'false',
              ),
              item_purchase_mode,
              item_stock_info: analytics.profileCase || '',
            },
          ],
        },
      })

      // Mark as tracked
      hasTrackedRef.current = true
    }
  }, [product, position, isAuthenticated, source, config, fetchAnalyticsData, isTrackingEnabled])

  return {
    trackProductPageAnalytics,
  }
}
